import { extractTextContent } from "./baseAdapter";
import GlobalStateManager from "../global-state/globalStateManager";
class LLMService {
    get model() {
        return this._model;
    }
    set model(value) {
        this._model = value;
    }
    constructor() {
        this._model = "";
    }
    getAdapterFromSettings() {
        return GlobalStateManager.getInstance().getAdapterFromSettings();
    }
    /**
     * Create LLM response (streaming)
     */
    async *createLLMResponse(messages, model, options) {
        const modelToUse = model || this._model;
        if (!modelToUse) {
            throw new Error("Model must be specified either as parameter or set on LLMService");
        }
        // Get adapter from settings instead of parsing model name
        const adapter = this.getAdapterFromSettings();
        if (!adapter) {
            throw new Error(`No adapter found. Please ensure an adapter is registered in GlobalStateManager.`);
        }
        const adapterOptions = {
            system: options?.system,
            tools: options?.tools,
            tool_choice: options?.tool_choice,
            max_tokens: options?.max_tokens,
            temperature: options?.temperature,
            extra_body: options?.extra_body,
            extra_headers: options?.extra_headers,
            thinking: options?.thinking
        };
        try {
            for await (const chunk of adapter.stream(modelToUse, messages, adapterOptions)) {
                const choice = chunk.choices?.[0];
                // Log tool calls if present
                if (choice?.delta?.tool_calls || choice?.message?.tool_calls) {
                    console.log('[LLMService] 🔧 Tool calls detected:', choice?.delta?.tool_calls || choice?.message?.tool_calls);
                }
                if (choice?.delta?.content) {
                    yield extractTextContent(choice.delta.content);
                }
                else if (choice?.message?.content) {
                    yield extractTextContent(choice.message.content);
                }
            }
        }
        catch (error) {
            console.error("Error in createLLMResponse:", error);
            throw error;
        }
    }
    /**
     * Create LLM response stream with full chunk data
     *
     * Use this when you need to handle tool calls or access full response metadata.
     * Yields complete LLMCompletionResponse objects instead of just text content.
     *
     * @example
     * ```typescript
     * import { createStreamProcessingState, accumulateToolCall } from '../globals/llm/streaming';
     *
     * const state = createStreamProcessingState();
     *
     * for await (const chunk of llmService.createLLMResponseStream(messages, model, options)) {
     *     const choice = chunk.choices?.[0];
     *
     *     // Handle text content
     *     if (choice?.delta?.content) {
     *         onTextChunk(choice.delta.content);
     *     }
     *
     *     // Handle tool calls
     *     if (choice?.delta?.tool_calls) {
     *         for (const toolCallDelta of choice.delta.tool_calls) {
     *             accumulateToolCall(state, toolCallDelta);
     *         }
     *     }
     *
     *     // Check finish reason
     *     if (choice?.finish_reason === 'tool_calls') {
     *         // Tool calls complete, ready to execute
     *     }
     * }
     * ```
     */
    async *createLLMResponseStream(messages, model, options) {
        const modelToUse = model || this._model;
        if (!modelToUse) {
            throw new Error("Model must be specified either as parameter or set on LLMService");
        }
        const adapter = this.getAdapterFromSettings();
        if (!adapter) {
            throw new Error(`No adapter found. Please ensure an adapter is registered in GlobalStateManager.`);
        }
        const adapterOptions = {
            system: options?.system,
            tools: options?.tools,
            tool_choice: options?.tool_choice,
            max_tokens: options?.max_tokens,
            temperature: options?.temperature,
            extra_body: options?.extra_body,
            extra_headers: options?.extra_headers,
            thinking: options?.thinking
        };
        try {
            for await (const chunk of adapter.stream(modelToUse, messages, adapterOptions)) {
                yield chunk; // Yield full chunk, not just content
            }
        }
        catch (error) {
            console.error("[LLMService] Error in createLLMResponseStream:", error);
            throw error;
        }
    }
    /**
     * Create LLM response (non-streaming)
     */
    async createLLMResponseComplete(messages, model, options) {
        const modelToUse = model || this._model;
        if (!modelToUse) {
            throw new Error("Model must be specified either as parameter or set on LLMService");
        }
        // Get adapter from settings instead of parsing model name
        const adapter = this.getAdapterFromSettings();
        if (!adapter) {
            throw new Error(`No adapter found. Please ensure an adapter is registered in GlobalStateManager.`);
        }
        const adapterOptions = {
            system: options?.system,
            tools: options?.tools,
            tool_choice: options?.tool_choice,
            max_tokens: options?.max_tokens,
            temperature: options?.temperature,
            extra_body: options?.extra_body,
            extra_headers: options?.extra_headers,
            thinking: options?.thinking
        };
        return await adapter.complete(modelToUse, messages, adapterOptions);
    }
}
export default LLMService;
